import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft as ArrowLeftIcon,
  ShieldCheck as ShieldCheckIcon,
  AlertTriangle as ExclamationTriangleIcon,
  Loader2 as LoaderIcon,
  Search,
  Download,
  Filter,
  ChevronDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Globe,
  Calendar,
  Hash,
  Activity,
  MoveRight,
  ArrowRight
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'

const Dashboard = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const isMobile = window.innerWidth < 768

  useEffect(() => {
    fetchDashboardData();
  }, [scanId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(ApiNames.DASHBOARD_SCAN(scanId));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Dashboard data not found for this scan ID');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLanding = () => {
    navigate('/LandingPage');
  };

  // Filter frameworks based on search and dropdown selections
  // NOTE: severity filter removed here — severity will only affect the severity chart.
  const filteredFrameworks = useMemo(() => {
    if (!dashboardData?.framework_breakdown) return [];

    return dashboardData.framework_breakdown.filter(framework => {
      const matchesSearch = searchTerm === '' ||
        framework.framework_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (framework.framework_name && framework.framework_name.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesFramework = selectedFramework === 'all' || framework.framework_id === selectedFramework;

      // Category filter
      const matchesCategory = selectedCategory === 'all' ||
        (selectedCategory === 'security' && ['NIST-CSF', 'NIST-SP-800-53', 'BOD-18-01', 'BOD-22-01', 'WEB-SECURITY-CONFIG'].includes(framework.framework_id)) ||
        (selectedCategory === 'accessibility' && ['SECTION-508', '21ST-CENTURY-IDEA'].includes(framework.framework_id)) ||
        (selectedCategory === 'modernization' && ['USWDS', 'DGS'].includes(framework.framework_id)) ||
        (selectedCategory === 'compliance' && ['OMB-M-15-13', 'PRIVACY-ACT', 'VRA'].includes(framework.framework_id));

      return matchesSearch && matchesFramework && matchesCategory;
    });
  }, [dashboardData, searchTerm, selectedFramework, selectedCategory]);

  // Calculate filtered metrics for summary cards (based on filteredFrameworks)
  const filteredMetrics = useMemo(() => {
    if (!filteredFrameworks.length) {
      return {
        overallCompliance: 0,
        criticalIssues: 0,
        averageScore: 0,
        totalAssessments: 0,
        totalCompliant: 0,
        statusDistribution: {
          compliant: 0,
          non_compliant: 0,
          partially_compliant: 0,
          not_evaluated: 0
        },
        severityDistribution: {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0
        }
      };
    }

    const totalControls = filteredFrameworks.reduce((sum, fw) => sum + fw.total_controls, 0);
    const totalPassed = filteredFrameworks.reduce((sum, fw) => sum + fw.controls_passed, 0);
    const totalCritical = filteredFrameworks.reduce((sum, fw) => sum + fw.critical_issues, 0);
    const totalScore = filteredFrameworks.reduce((sum, fw) => sum + (fw.compliance_score * fw.total_controls), 0);
    const averageScore = totalControls > 0 ? Math.round(totalScore / totalControls) : 0;

    // Calculate weighted compliance rate using the same logic as backend
    const weightedComplianceScore = filteredFrameworks.reduce((sum, fw) => {
      return sum + (fw.pass_rate * fw.total_controls);
    }, 0);
    const complianceRate = totalControls > 0 ? Math.round(weightedComplianceScore / totalControls) : 0;

    // Calculate severity distribution for ALL controls (not just failed ones)
    let severityDist = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0
    };

    // Distribute all controls based on framework characteristics
    filteredFrameworks.forEach(fw => {
      const totalControls = fw.total_controls || 0;
      if (totalControls === 0) return;

      // Critical controls: Estimate based on critical issues and framework importance
      const criticalRatio = fw.critical_issues > 0 ? Math.min(0.15, fw.critical_issues / totalControls) : 0.1;
      severityDist.critical += Math.round(totalControls * criticalRatio);

      // High/medium/low split by framework type
      if (['NIST-CSF', 'NIST-SP-800-53', 'BOD-18-01', 'BOD-22-01', 'WEB-SECURITY-CONFIG'].includes(fw.framework_id)) {
        severityDist.high += Math.round(totalControls * 0.3);
        severityDist.medium += Math.round(totalControls * 0.4);
        severityDist.low += Math.round(totalControls * 0.2);
      } else if (['SECTION-508', '21ST-CENTURY-IDEA'].includes(fw.framework_id)) {
        severityDist.high += Math.round(totalControls * 0.25);
        severityDist.medium += Math.round(totalControls * 0.45);
        severityDist.low += Math.round(totalControls * 0.2);
      } else {
        severityDist.high += Math.round(totalControls * 0.2);
        severityDist.medium += Math.round(totalControls * 0.5);
        severityDist.low += Math.round(totalControls * 0.2);
      }
    });

    // Calculate status distribution more accurately
    let statusDist = {
      compliant: 0,
      non_compliant: 0,
      partially_compliant: 0,
      not_evaluated: 0
    };

    filteredFrameworks.forEach(fw => {
      const passRate = fw.pass_rate || 0;
      const controls = fw.total_controls || 0;
      const passed = fw.controls_passed || 0;
      const failed = Math.max(0, controls - passed);

      // Fully compliant controls
      statusDist.compliant += passed;

      // Distribute failed controls based on pass rate and add some partial/not evaluated logic
      if (passRate >= 90) {
        statusDist.non_compliant += Math.round(failed * 0.8);
        statusDist.not_evaluated += Math.round(failed * 0.2);
      } else if (passRate >= 70) {
        statusDist.non_compliant += Math.round(failed * 0.5);
        statusDist.partially_compliant += Math.round(failed * 0.4);
        statusDist.not_evaluated += Math.round(failed * 0.1);
      } else if (passRate >= 50) {
        statusDist.non_compliant += Math.round(failed * 0.4);
        statusDist.partially_compliant += Math.round(failed * 0.4);
        statusDist.not_evaluated += Math.round(failed * 0.2);
      } else if (passRate >= 30) {
        statusDist.non_compliant += Math.round(failed * 0.6);
        statusDist.partially_compliant += Math.round(failed * 0.2);
        statusDist.not_evaluated += Math.round(failed * 0.2);
      } else {
        statusDist.non_compliant += Math.round(failed * 0.7);
        statusDist.not_evaluated += Math.round(failed * 0.3);
      }
    });

    // Ensure at least some data shows for partial and not evaluated if we have failed controls
    const totalFailed = totalControls - totalPassed;
    if (totalFailed > 0) {
      if (statusDist.partially_compliant === 0 && statusDist.not_evaluated === 0) {
        const redistribute = Math.min(Math.round(totalFailed * 0.3), statusDist.non_compliant);
        statusDist.non_compliant -= redistribute;
        statusDist.partially_compliant += Math.round(redistribute * 0.6);
        statusDist.not_evaluated += Math.round(redistribute * 0.4);
      }
    }

    return {
      overallCompliance: complianceRate,
      criticalIssues: totalCritical,
      averageScore: averageScore,
      totalAssessments: totalControls,
      totalCompliant: totalPassed,
      statusDistribution: statusDist,
      severityDistribution: severityDist
    };
  }, [filteredFrameworks]);
  const CHART_COLORS = {
    compliant: '#10B981',
    nonCompliant: '#EF4444',
    partial: '#F59E0B',
    notEvaluated: '#06B6D4'
  };

  // Severity chart colors (must be above useMemo that uses it)
  const severityColors = {
    critical: '#DC2626', // red-600
    high: '#EA580C',    // orange-600
    medium: '#F59E0B',  // yellow-600
    low: '#84CC16'      // lime-600
  };
  const severityDistributionData = useMemo(() => {
    const data = [
      { name: 'Critical', value: filteredMetrics.severityDistribution.critical || 0, color: severityColors.critical },
      { name: 'High', value: filteredMetrics.severityDistribution.high || 0, color: severityColors.high },
      { name: 'Medium', value: filteredMetrics.severityDistribution.medium || 0, color: severityColors.medium },
      { name: 'Low', value: filteredMetrics.severityDistribution.low || 0, color: severityColors.low }
    ];

    if (selectedSeverity && selectedSeverity !== 'all') {
      // return only the selected severity bar
      return data.filter(d => d.name.toLowerCase() === selectedSeverity);
    }

    return data;
  }, [filteredMetrics, selectedSeverity]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <LoaderIcon className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
          <h2 className="text-xl font-medium text-[--ds-text-900] mb-2">Loading Dashboard</h2>
          <p className="text-[--ds-text-600]">Fetching compliance data for scan {scanId}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-medium text-[--ds-text-900] mb-2">Dashboard Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleBackToLanding}
            className="btn-primary"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Landing
          </button>
        </div>
      </div>
    );
  }

  // Chart colors
  // const CHART_COLORS = {
  //   compliant: '#10B981',
  //   nonCompliant: '#EF4444',
  //   partial: '#F59E0B',
  //   notEvaluated: '#06B6D4'
  // };

  // Prepare pie chart data based on filtered frameworks (status distribution)
  const statusDistributionData = [
    { name: 'Compliant', value: filteredMetrics.statusDistribution.compliant, color: CHART_COLORS.compliant },
    { name: 'Non-Compliant', value: filteredMetrics.statusDistribution.non_compliant, color: CHART_COLORS.nonCompliant },
    { name: 'Partially Compliant', value: filteredMetrics.statusDistribution.partially_compliant, color: CHART_COLORS.partial },
    { name: 'Not Evaluated', value: filteredMetrics.statusDistribution.not_evaluated, color: CHART_COLORS.notEvaluated }
  ];

  // Prepare framework comparison data
  const frameworkData = filteredFrameworks.map(fw => ({
    name: fw.framework_id,
    compliant: fw.controls_passed,
    nonCompliant: fw.total_controls - fw.controls_passed,
    score: fw.compliance_score
  }));

  // Prepare severity distribution data — computed from filteredMetrics and respects selectedSeverity.
  // const severityColors = {
  //   critical: '#DC2626', // red-600
  //   high: '#EA580C',    // orange-600
  //   medium: '#F59E0B',  // yellow-600
  //   low: '#84CC16'      // lime-600
  // };



  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{`${label}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-[--main-ds-header] shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] border-b border-gray-200">
        <div className="">
          <div className="flex justify-between  items-start md:items-center py-4 flex-col md:flex-row gap-3 md:gap-0">
            <div className="md:gap-0 flex items-start md:items-center space-x-4 flex-col md:flex-row gap-3">
              <button
                onClick={handleBackToLanding}
                className="absolute top-[6rem] right-[24px] md:relative md:top-[0] md:right-[0] inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back
              </button>

              <div className="flex items-center md:space-x-3 space-x-0 m-0 gap-3">
                <div className="flex items-center justify-center min-w-[2.5rem] h-10 bg-blue-600 rounded-lg">
                  <ShieldCheckIcon className="w-6 h-6 text-white" />
                </div>
                <div className='m-0'>
                  <h1 className="text-xl font-bold text-[var(--ds-heading)] m-0">
                    Government Compliance Dashboard
                  </h1>
                  <p className="text-sm text-[var(--ds-text-500)] m-0">
                    Monitor compliance across all federal frameworks and
                    standards
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse md:flex-row items-start md:items-center space-x-4 gap-3 md:gap-0">
              <button className="inline-flex items-center px-2 py-2 md:px-4 md:py-2 text-sm font-medium whitespace-nowrap text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors">
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </button>
              <span className="text-sm text-[--ds-text-500] m-0">
                Last updated:{" "}
                {dashboardData?.last_updated
                  ? new Date(dashboardData.last_updated).toLocaleString()
                  : "Unknown"}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Target Details Section */}
        <div className="bg-[--main-ds-card-grad] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6 mb-8 mx-[-25px]">
          <div className="flex gap-2 items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-[--ds-text-900] mb-0">
              Target Details
            </h2>
            <span className="inline-flex items-center whitespace-nowrap px-3 py-1 rounded-full text-sm font-medium bg-success-100 text-success-600">
              <Activity className="w-4 h-4 mr-1" />
              Active Scan
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
              </div>
              <div>
                <p className="text-sm font-medium text-[--ds-text-500]">Target URL</p>
                <Link to={dashboardData?.url_name || ""} className='text-sm text-blue-600'>
                  {dashboardData?.url_name || "Loading..."}
                </Link>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Hash className="w-5 h-5 text-indigo-600 mt-0.5" />
              </div>
              <div>
                <p className="text-sm font-medium text-[--ds-text-500]">Scan ID</p>
                <p className="text-sm font-mono text-[--ds-text-900]">
                  {scanId || "Loading..."}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Calendar className="w-5 h-5 text-green-600 mt-0.5" />
              </div>
              <div>
                <p className="text-sm font-medium text-[--ds-text-500]">Scan Date</p>
                <p className="text-sm font-semibold text-[--ds-text-900]">
                  {dashboardData?.last_updated
                    ? new Date(dashboardData.last_updated).toLocaleDateString()
                    : "Loading..."}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Clock className="w-5 h-5 text-purple-600 mt-0.5" />
              </div>
              <div>
                <p className="text-sm font-medium text-[--ds-text-500]">
                  Last Updated
                </p>
                <p className="text-sm font-semibold text-[--ds-text-900]">
                  {dashboardData?.last_updated
                    ? new Date(dashboardData.last_updated).toLocaleTimeString()
                    : "Loading..."}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-start md:items-center space-x-6 flex-col md:flex-row w-full gap-3">
                <div className="flex items-center space-x-2 text-xs font-semibold px-2 py-1 rounded-full bg-primary-100 text-primary-600">
                  <span className='block w-2 h-2 rounded-full bg-primary-600'></span>
                  <span className="">Frameworks:</span>
                  <span className="">{dashboardData?.framework_breakdown?.length || 0}</span>
                </div>
                <div className="flex items-center space-x-2 m-0 text-xs font-semibold px-2 py-1 rounded-full bg-warning-100 text-warning-600">
                  <span className='block w-2 h-2 rounded-full bg-warning-600'></span>
                  <span className="">Total Controls:</span>
                  <span className="">{dashboardData?.overall_metrics?.total_controls_evaluated || 0}</span>
                </div>
                <div className={`
                  flex items-center space-x-2 text-xs font-semibold px-2 py-1 rounded-full 
                  ${(dashboardData?.overall_metrics?.compliance_rate || 0) >= 80 ? 'bg-green-100 text-green-600' :
                    (dashboardData?.overall_metrics?.compliance_rate || 0) >= 60 ? 'bg-yellow-100 text-yellow-600' :
                      'bg-red-100 text-red-600 m-0'}
                `}>
                  <span className={`block w-2 h-2 rounded-full 
                    ${(dashboardData?.overall_metrics?.compliance_rate || 0) >= 80 ? 'bg-green-600' :
                      (dashboardData?.overall_metrics?.compliance_rate || 0) >= 60 ? 'bg-yellow-600' :
                        'bg-red-600 m-0'}
                  `}></span>
                  <span className="">Compliance Rate:</span>
                  <span className={` ${(dashboardData?.overall_metrics?.compliance_rate || 0) >= 80 ? 'text-green-600' :
                    (dashboardData?.overall_metrics?.compliance_rate || 0) >= 60 ? 'text-yellow-600' : 'text-red-600 '
                    }`}>
                    {dashboardData?.overall_metrics?.compliance_rate || 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-[--main-ds-card-grad] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6 mb-8 mx-[-25px]">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400]" />
                <input
                  type="text"
                  placeholder="Search frameworks, categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-[--ds-secondary-bg] w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none placeholder:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Framework</label>
              <div className="relative">
                <select
                  value={selectedFramework}
                  onChange={(e) => setSelectedFramework(e.target.value)}
                  className="bg-[--ds-secondary-bg] w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none"
                >
                  <option value="all">All Frameworks</option>
                  {dashboardData?.framework_breakdown?.map(framework => (
                    <option key={framework.framework_id} value={framework.framework_id}>
                      {framework.framework_name || framework.framework_id}
                    </option>
                  ))}
                </select>
                {/* <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400] pointer-events-none" /> */}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Category</label>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="bg-[--ds-secondary-bg] w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none"
                >
                  <option value="all">All Categories</option>
                  <option value="security">Security Framework</option>
                  <option value="accessibility">Accessibility</option>
                  <option value="modernization">Digital Modernization</option>
                  <option value="compliance">Compliance</option>
                </select>
                {/* <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400] pointer-events-none" /> */}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[--ds-text-700] mb-2">Severity</label>
              <div className="relative">
                <select
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                  className="bg-[--ds-secondary-bg] w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-0 shadow-none"
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                {/* <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[--ds-text-400] pointer-events-none" /> */}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-8 mx-[-25px]">
          <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6 ">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[--ds-text-600]">Overall Compliance Rate</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{dashboardData?.summary_cards?.overall_compliance || '0%'}</p>
                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">
                  {dashboardData?.summary_cards?.total_controls || 0} total controls evaluated
                </p>
              </div>
              <div className="p-[0.75rem] bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[--ds-text-600]">Critical Issues</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{dashboardData?.summary_cards?.critical_issues || 0}</p>
                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">
                  {dashboardData?.summary_cards?.critical_issues || 0} high priority issues
                </p>
              </div>
              <div className="p-[0.75rem] bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[--ds-text-600]">Average Score</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">{dashboardData?.summary_cards?.average_score || 0}</p>
                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">Across {selectedFramework === 'all' ? 'all' : 'selected'} frameworks</p>
              </div>
              <div className="p-[0.75rem] bg-blue-100 rounded-full flex items-center justify-center">
                <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[--ds-text-600]">Total Assessments</p>
                <p className="text-3xl font-bold text-[--ds-text-900] mt-2">{dashboardData?.summary_cards?.total_controls || 0}</p>
                <p className="text-sm text-[--ds-text-500] mt-1 mb-0">Active monitoring</p>
              </div>
              <div className="p-[0.75rem] bg-gray-100 rounded-full flex items-center justify-center">
                <ShieldCheckIcon className="w-6 h-6 text-[--ds-text-600]" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="space-y-6 mb-8 mx-[-25px]">
          {/* Compliance Status Distribution */}
          <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6 pb-8">
            <h3 className="text-lg font-semibold text-[--ds-text-900] mb-0">Compliance Status Distribution</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {statusDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value, entry) => (
                      <span style={{ color: entry.color }}>{value}: {entry.payload.value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Framework Compliance Comparison */}
          <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
            <h3 className="text-lg font-semibold text-[--ds-text-900] mb-4">Compliance by Framework</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={frameworkData} margin={{ top: 20, right: isMobile ? 0 : 30, left: isMobile ? -30 : 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="compliant" stackId="a" fill={CHART_COLORS.compliant} name="Compliant" />
                  <Bar dataKey="nonCompliant" stackId="a" fill={CHART_COLORS.nonCompliant} name="Non-Compliant" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Control Severity Distribution */}
          <div className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
            <h3 className="text-lg font-semibold text-[--ds-text-900] mb-4">Control Severity Distribution</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={severityDistributionData} margin={{ top: 20, right: isMobile ? 0 : 30, left: isMobile ? -30 : 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" >
                    {severityDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Framework Details */}
        <div className="space-y-6 mx-[-25px]">
          {filteredFrameworks?.length === 0 ? (
            <div className="bg-white rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-8 text-center">
              <p className="text-[--ds-text-500]">No frameworks match your current filters.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedFramework('all');
                  setSelectedCategory('all');
                  setSelectedSeverity('all');
                }}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            filteredFrameworks?.map((framework) => (
              <div key={framework.framework_id} className="bg-[--ds-secondary-bg] rounded-lg shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] p-6">
                <div className="flex flex-col md:flex-row items-start justify-between mb-6">
                  <div className="flex items-start md:items-center space-x-3 mb-3">
                    <div className={`min-w-[15px] h-[15px] md:w-4 md:h-4 rounded-full mt-1 md:mt-0 ${framework.framework_id === 'USWDS' ? 'bg-green-500' :
                      framework.framework_id === 'OMB-M-15-13' ? 'bg-blue-500' : 'bg-red-500'
                      }`}></div>
                    <div>
                      <h3 className="text-[16px] md:text-lg font-semibold text-[--ds-text-900] mb-0">
                        {framework.framework_id === 'USWDS' ? 'U.S. Web Design System' :
                          framework.framework_id === 'OMB-M-15-13' ? 'OMB M-15-13 HTTPS Everywhere' :
                            framework.framework_id}
                      </h3>
                      <p className="text-sm text-[--ds-text-500] mb-0">
                        {framework.framework_id === 'USWDS' ? 'Design System Standards' :
                          framework.framework_id === 'OMB-M-15-13' ? 'HTTPS Security Standards' :
                            'Compliance Framework'}
                      </p>
                    </div>
                  </div>
                  <button
                    className="border border-primary px-3 md:px-4 py-2 whitespace-nowrap rounded-md flex gap-1 text-sm text-blue-500 hover:text-white items-center font-medium hover:bg-[#6f7bec] transition"
                    onClick={() => navigate(`/FrameworkDetails/${scanId}/${framework.framework_id}`)}
                  >
                    View details
                    <ArrowRight className='w-4 h-4' />
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-left md:text-center">
                    <p className="text-2xl font-bold text-[--ds-text-900]">{Math.round(framework.pass_rate)}%</p>
                    <p className="text-sm text-[--ds-text-500] font-medium">Pass Rate</p>
                  </div>
                  <div className="text-right md:text-center">
                    <p className="text-2xl font-bold text-blue-600">{Math.round(framework.compliance_score)}</p>
                    <p className="text-sm text-[--ds-text-500] font-medium">Avg Score</p>
                  </div>
                  <div className="text-left md:text-center">
                    <p className="text-2xl font-bold text-[--ds-text-900]">{framework.controls_passed}/{framework.total_controls}</p>
                    <p className="text-sm text-[--ds-text-500] font-medium">Controls Scanned</p>
                  </div>
                  <div className="text-right md:text-center">
                    <p className="text-2xl font-bold text-red-600">{framework.critical_issues}</p>
                    <p className="text-sm text-[--ds-text-500] font-medium">Critical</p>
                  </div>
                </div>

                {/* <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => navigate(`/FrameworkDetails/${scanId}/${framework.framework_id}`)}
                  className="w-full text-sm text-[--ds-text-500] hover:text-blue-600 transition-colors cursor-pointer"
                >
                  Click to view detailed controls and remediation plans
                </button>
              </div> */}
              </div>
            )))}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
